// File: src/pages/Inventory/UsageLogs.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, RefreshCcw, Search, SlidersHorizontal } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";

/* ───────────────── helpers ───────────────── */
const robustArr = (x, f = []) => (Array.isArray(x) ? x : f);
const fmt = new Intl.NumberFormat();
const fmtNum = (v) => fmt.format(Number(v || 0));

function useDebounced(val, ms = 400) {
  const [v, setV] = useState(val);
  useEffect(() => { const t = setTimeout(() => setV(val), ms); return () => clearTimeout(t); }, [val, ms]);
  return v;
}

function csvEscape(s) {
  const v = String(s ?? "");
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
function downloadCSV(rows, name = "usage_logs.csv") {
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}

/* ───────────────── mock data ───────────────── */
function makeMock(start, end) {
  const dayMs = 86400000;
  const d0 = new Date(start || Date.now() - 29 * dayMs);
  const days = 30;
  const series = Array.from({ length: days }, (_, i) => {
    const d = new Date(d0.getTime() + i * dayMs);
    const used = Math.max(0, Math.round(80 + Math.sin(i / 3) * 40 + Math.random() * 30));
    return { date: d.toISOString().slice(0, 10), used };
  });
  const logs = Array.from({ length: 150 }, (_, i) => {
    const d = new Date(d0.getTime() + Math.floor(Math.random() * days) * dayMs);
    const actions = ["Issue", "Receive", "Adjust-", "Adjust+"];
    const action = actions[Math.floor(Math.random() * actions.length)];
    const qty = Math.ceil(Math.random() * 50) * (action === "Adjust-" || action === "Issue" ? -1 : 1);
    const items = [
      { code: "FRU-001", name: "Bananas 18kg" },
      { code: "FRU-002", name: "Apples 1kg" },
      { code: "VEG-001", name: "Tomatoes 5kg" },
      { code: "DRY-013", name: "Pasta 500g" },
      { code: "BEV-020", name: "Cola 330ml" },
    ];
    const it = items[Math.floor(Math.random() * items.length)];
    const locs = ["Main Store", "Central Kitchen", "Bar"];
    return {
      id: i + 1,
      ts: d.toISOString().slice(0, 19).replace("T", " "),
      item_code: it.code,
      item_name: it.name,
      location: locs[Math.floor(Math.random() * locs.length)],
      action,
      quantity: qty,
      ref: action === "Receive" ? `PO-${1000 + i}` : action === "Issue" ? `EVT-${200 + i}` : "",
      user: ["Alice", "Bob", "Sam"][Math.floor(Math.random() * 3)],
    };
  });
  return {
    kpis: { totalMovements: logs.length, issued: logs.filter(l=>l.quantity<0).reduce((a,b)=>a+Math.abs(b.quantity),0), received: logs.filter(l=>l.quantity>0).reduce((a,b)=>a+b.quantity,0), adjustments: logs.filter(l=>String(l.action).startsWith("Adjust")).length },
    usageSeries: series,
    logs,
    locations: ["Main Store", "Central Kitchen", "Bar"],
    actions: ["Issue", "Receive", "Adjust-", "Adjust+"],
  };
}

/* ───────────────── main ───────────────── */
export default function UsageLogs() {
  const [start, setStart] = useState(() => new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10));
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [location, setLocation] = useState("");
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const qDeb = useDebounced(q);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(() => makeMock());

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { start, end, location, action, q: qDeb, page, pageSize };
      const res = await api.get("/solo-mdsh-api/reports/inventory/usage-logs", { params }).catch(() => null);
      if (res?.data?.logs) setData(res.data); else setData(makeMock(start, end));
    } catch (e) {
      console.warn("UsageLogs fallback → mock", e);
      setData(makeMock(start, end));
      setError("Using demo data (API unavailable)");
    } finally { setLoading(false); }
  }, [start, end, location, action, qDeb, page, pageSize]);

  useEffect(() => { setPage(1); }, [start, end, location, action, qDeb]);
  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};
  const series = robustArr(data?.usageSeries);
  const allLogs = robustArr(data?.logs);
  const locations = robustArr(data?.locations);
  const actions = robustArr(data?.actions);

  // client-side paginate if backend returned full set (mock path)
  const paginated = useMemo(() => {
    if (allLogs.length <= pageSize) return allLogs;
    const startIdx = (page - 1) * pageSize;
    return allLogs.slice(startIdx, startIdx + pageSize);
  }, [allLogs, page, pageSize]);
  const totalPages = Math.max(1, Math.ceil(allLogs.length / pageSize));

  const exportCSV = useCallback(() => {
    const rows = [["ts","item_code","item_name","location","action","quantity","ref","user"], ...allLogs.map(l=>[l.ts,l.item_code,l.item_name,l.location,l.action,l.quantity,l.ref,l.user])];
    downloadCSV(rows, `usage_logs_${start}_to_${end}.csv`);
  }, [allLogs, start, end]);

  return (
    <div className="min-h-screen bg-[#090d1a] text-white">
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
              <SlidersHorizontal className="h-6 w-6 text-emerald-400"/> Usage Logs
            </h1>
            <p className="text-sm text-white/60 mt-1">Track all inventory consumption, receipts, and adjustments.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={exportCSV} className="gap-2"><Download className="h-4 w-4"/>Export CSV</Button>
            <Button variant="outline" onClick={load} className="gap-2"><RefreshCcw className="h-4 w-4"/>Refresh</Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
          <div>
            <label className="block text-xs text-white/60 mb-1">Start</label>
            <Input type="date" value={start} onChange={(e)=>setStart(e.target.value)} className="bg-white/5 border-white/10"/>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">End</label>
            <Input type="date" value={end} onChange={(e)=>setEnd(e.target.value)} className="bg-white/5 border-white/10"/>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Location</label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue placeholder="All"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="">All</SelectItem>
                {locations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Action</label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue placeholder="All"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="">All</SelectItem>
                {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-white/60 mb-1">Search</label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-white/40"/>
              <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Item code, name, reference, user" className="pl-8 bg-white/5 border-white/10"/>
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Page size</label>
            <Select value={String(pageSize)} onValueChange={(v)=>{setPageSize(Number(v)); setPage(1);}}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue/></SelectTrigger>
              <SelectContent>
                {[10,25,50,100].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Total Movements" value={fmtNum(k.totalMovements)} />
          <KPI label="Quantity Issued" value={fmtNum(k.issued)} />
          <KPI label="Quantity Received" value={fmtNum(k.received)} />
          <KPI label="Adjustments" value={fmtNum(k.adjustments)} />
        </div>

        {/* Usage Over Time */}
        <Card className="mt-6 bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="text-sm font-medium text-white/80 mb-2">Usage Over Time</div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ left: 8, right: 8, top: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gUsed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34d399" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: "#94a3b8" }} interval={Math.floor(series.length/6)} />
                  <YAxis tick={{ fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ background: "#0b1221", border: "1px solid rgba(255,255,255,.1)" }} />
                  <Area type="monotone" dataKey="used" stroke="#34d399" fill="url(#gUsed)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="mt-6 bg-white/5 border-white/10">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white/[.06] backdrop-blur z-10">
                  <tr className="border-b border-white/10 text-white/70">
                    <Th>Timestamp</Th>
                    <Th>Item</Th>
                    <Th>Location</Th>
                    <Th>Action</Th>
                    <Th className="text-right">Quantity</Th>
                    <Th>Reference</Th>
                    <Th>User</Th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((l) => (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/[.03]">
                      <Td>{l.ts}</Td>
                      <Td>
                        <div className="font-medium">{l.item_code}</div>
                        <div className="text-white/60 text-xs">{l.item_name}</div>
                      </Td>
                      <Td>{l.location}</Td>
                      <Td>{l.action}</Td>
                      <Td className="text-right">
                        <span className={Number(l.quantity) < 0 ? "text-rose-300" : "text-emerald-300"}>
                          {Number(l.quantity) < 0 ? "−" : "+"}{fmtNum(Math.abs(l.quantity))}
                        </span>
                      </Td>
                      <Td>{l.ref || ""}</Td>
                      <Td>{l.user}</Td>
                    </tr>
                  ))}
                  {paginated.length === 0 && (
                    <tr><td className="py-8 text-center text-white/50" colSpan={7}>No logs for the selected filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-xs text-white/60">Page {page} of {totalPages} • {fmtNum(allLogs.length)} rows</div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}><ChevronLeft className="h-4 w-4"/></Button>
                <Button variant="outline" size="sm" disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}><ChevronRight className="h-4 w-4"/></Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && <p className="mt-3 text-xs text-amber-400/90">{error}</p>}
        {loading && <p className="mt-1 text-xs text-white/60">Loading…</p>}
      </div>
    </div>
  );
}

/* ───────────────── atoms ───────────────── */
function Th({ children, className }) {
  return <th className={"text-left text-xs font-medium uppercase tracking-wide px-4 py-2 "+(className||"")}>{children}</th>;
}
function Td({ children, className }) {
  return <td className={"px-4 py-2 "+(className||"")}>{children}</td>;
}

function KPI({ label, value }) {
  return (
    <Card className="bg-gradient-to-br from-white/[.06] to-white/[.02] border-white/10 backdrop-blur">
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-white/60">{label}</div>
        <div className="text-2xl font-semibold mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
